// Pure ISO-date helpers for the time-shaped board axes (design brief §5.2). NO `new Date()` over the
// ambient clock — every "today" comes from the projection's asOfDate (deterministic; null omits the
// today line honestly, never a fabricated now). Dates are ISO `YYYY-MM-DD` strings compared as
// day-number triples; the month grid, the timeline extent and the bar geometry all derive from them.
// No JSX, no fetching — arithmetic only, so both the calendar and the timeline share one boundary.

import type { WorkDateRange, WorkItemRow } from '../projections.ts';

/** A parsed ISO day as its three parts (comparable without a Date object). */
export interface DayParts {
  readonly year: number;
  readonly month: number; // 1–12
  readonly day: number; // 1–31
}

/** Parse an ISO `YYYY-MM-DD` into comparable parts; null on any non-conforming string. */
export function parseIsoDay(iso: string | null): DayParts | null {
  if (iso === null) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

/** A day as a single ordinal for ordering/subtraction (proleptic, calendar-agnostic — good enough
 *  for comparisons and pixel spans across the small windows a board carries). */
function ordinal(parts: DayParts): number {
  return parts.year * 372 + (parts.month - 1) * 31 + (parts.day - 1);
}

/** Compare two ISO days: negative if a<b, 0 if equal, positive if a>b. Nulls sort last. */
export function compareIsoDay(a: string | null, b: string | null): number {
  const pa = parseIsoDay(a);
  const pb = parseIsoDay(b);
  if (pa === null && pb === null) return 0;
  if (pa === null) return 1;
  if (pb === null) return -1;
  return ordinal(pa) - ordinal(pb);
}

/** True when `day` falls strictly before `other` (both ISO; a null day is never "before"). */
export function isBefore(day: string | null, other: string | null): boolean {
  if (parseIsoDay(day) === null || parseIsoDay(other) === null) return false;
  return compareIsoDay(day, other) < 0;
}

// --- CALENDAR MONTH GRID -----------------------------------------------------------------------------

/** One month anchor (year + 1–12 month) the calendar grid is built for. */
export interface MonthAnchor {
  readonly year: number;
  readonly month: number;
}

/** Days in a Gregorian month (leap-aware) — the grid needs the real trailing count. */
export function daysInMonth(anchor: MonthAnchor): number {
  const { year, month } = anchor;
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/** The weekday (0=Sun … 6=Sat) the given ISO day lands on — Sakamoto's algorithm, no Date. */
export function weekdayOf(anchor: MonthAnchor, day: number): number {
  const t = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  let y = anchor.year;
  const m = anchor.month;
  if (m < 3) y -= 1;
  const shift = t[m - 1] ?? 0;
  return (y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) + shift + day) % 7;
}

/** One cell of the month grid: a real day (ISO + weekday index) or a leading/trailing blank. */
export interface CalendarCell {
  readonly iso: string | null;
  readonly day: number | null;
}

/** Build the month grid as weeks × 7 cells, Sunday-first, with leading/trailing blanks so every
 *  week is full. Deterministic from the anchor alone. */
export function monthGrid(anchor: MonthAnchor): CalendarCell[][] {
  const total = daysInMonth(anchor);
  const lead = weekdayOf(anchor, 1);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < lead; i += 1) cells.push({ iso: null, day: null });
  for (let d = 1; d <= total; d += 1) {
    const mm = String(anchor.month).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    cells.push({ iso: `${anchor.year}-${mm}-${dd}`, day: d });
  }
  while (cells.length % 7 !== 0) cells.push({ iso: null, day: null });
  const weeks: CalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Step a month anchor by ±1, rolling the year (December → January). */
export function stepMonth(anchor: MonthAnchor, delta: 1 | -1): MonthAnchor {
  const raw = anchor.month + delta;
  if (raw < 1) return { year: anchor.year - 1, month: 12 };
  if (raw > 12) return { year: anchor.year + 1, month: 1 };
  return { year: anchor.year, month: raw };
}

/** The anchor month a view opens on: asOfDate's month, else the earliest dated item's month,
 *  else null (nothing dated and no as-of — the caller shows only the undated tray). */
export function anchorMonth(
  asOfDate: string | null,
  isoDays: readonly (string | null)[],
): MonthAnchor | null {
  const fromAsOf = parseIsoDay(asOfDate);
  if (fromAsOf !== null) return { year: fromAsOf.year, month: fromAsOf.month };
  const dated = isoDays
    .map((iso) => parseIsoDay(iso))
    .filter((p): p is DayParts => p !== null)
    .sort((a, b) => ordinal(a) - ordinal(b));
  const first = dated[0];
  return first !== undefined ? { year: first.year, month: first.month } : null;
}

// --- PARTITION DATED vs UNDATED ----------------------------------------------------------------------

/** Split a slice into calendar-dated (a dateField) and undated (the calendar's tray). */
export function partitionByDateField(items: readonly WorkItemRow[]): {
  readonly dated: WorkItemRow[];
  readonly undated: WorkItemRow[];
} {
  const dated: WorkItemRow[] = [];
  const undated: WorkItemRow[] = [];
  for (const item of items) {
    if (parseIsoDay(item.dateField) !== null) dated.push(item);
    else undated.push(item);
  }
  return { dated, undated };
}

/** Split a slice into timeline-windowed (a dateRange with a valid start) and undated (the tray). */
export function partitionByDateRange(items: readonly WorkItemRow[]): {
  readonly windowed: WorkItemRow[];
  readonly undated: WorkItemRow[];
} {
  const windowed: WorkItemRow[] = [];
  const undated: WorkItemRow[] = [];
  for (const item of items) {
    if (item.dateRange !== null && parseIsoDay(item.dateRange.start) !== null) windowed.push(item);
    else undated.push(item);
  }
  return { windowed, undated };
}

// --- TIMELINE EXTENT + BAR GEOMETRY ------------------------------------------------------------------

/** The timeline's date extent as ordinals (inclusive). An open end extends the extent to asOfDate
 *  or the widest start, whichever is later — so an overdue open bar always has room to draw. */
export interface TimelineExtent {
  readonly min: number;
  readonly max: number;
}

/** Compute the extent covering every windowed bar plus the today marker. Null when nothing spans. */
export function timelineExtent(
  items: readonly WorkItemRow[],
  asOfDate: string | null,
): TimelineExtent | null {
  const points: number[] = [];
  const push = (iso: string | null): void => {
    const parts = parseIsoDay(iso);
    if (parts !== null) points.push(ordinal(parts));
  };
  for (const item of items) {
    const range: WorkDateRange = item.dateRange;
    if (range === null) continue;
    push(range.start);
    push(range.end);
  }
  push(asOfDate);
  if (points.length === 0) return null;
  return { min: Math.min(...points), max: Math.max(...points) };
}

/** A bar's position as fractions [0,1] of the extent: `left` and `width`. An open bar (null end)
 *  runs to the extent's right edge and is flagged `open`. A zero-span extent draws a hairline. */
export interface BarGeometry {
  readonly left: number;
  readonly width: number;
  readonly open: boolean;
}

/** Position/width a range within an extent. The open end clamps to the extent's right edge. */
export function barGeometry(range: WorkDateRange, extent: TimelineExtent): BarGeometry | null {
  if (range === null) return null;
  const start = parseIsoDay(range.start);
  if (start === null) return null;
  const span = extent.max - extent.min;
  const startPos = ordinal(start);
  const endParts = parseIsoDay(range.end);
  const open = endParts === null;
  const endPos = open ? extent.max : ordinal(endParts);
  if (span <= 0) return { left: 0, width: 1, open };
  const left = (startPos - extent.min) / span;
  const rawRight = (endPos - extent.min) / span;
  const width = Math.max(rawRight - left, 0);
  return { left, width, open };
}

/** The today marker's fractional position within the extent; null when asOfDate is null (honest —
 *  no today line without a real as-of). */
export function todayPosition(asOfDate: string | null, extent: TimelineExtent): number | null {
  const parts = parseIsoDay(asOfDate);
  if (parts === null) return null;
  const span = extent.max - extent.min;
  if (span <= 0) return 0;
  return (ordinal(parts) - extent.min) / span;
}

/** Whether an item is overdue: its window has slipped past the as-of AND it is not in an exit state.
 *  A closed window is late when its end is before as-of; an open window is late when its start is. */
export function isOverdue(
  item: WorkItemRow,
  asOfDate: string | null,
  exitStates: ReadonlySet<string>,
): boolean {
  if (asOfDate === null || item.dateRange === null) return false;
  if (exitStates.has(item.state)) return false;
  const boundary = item.dateRange.end ?? item.dateRange.start;
  return isBefore(boundary, asOfDate);
}
